// I-DT · Nguồn thư thật cho Box qua `@hana/graph` (Hub truyền client vào bằng `setBoxGraph`).
// Vùng không import code của Hub / package khác ngoài hợp đồng → dùng kiểu cấu trúc tối thiểu, khớp `MailSummary` của `@hana/graph`.
// Chỉ đọc; địa chỉ che như board; không log nội dung thư. Lỗi / chưa đăng nhập → giữ dữ liệu mẫu, kèm trạng thái cho màn.
import { MAILS } from './fixtures';
import type { BoxMail } from './types';

export interface GraphMailSummary {
  id: string;
  subject: string | null;
  bodyPreview: string;
  from?: { emailAddress: { address: string; name?: string } };
  receivedDateTime: string;
  isRead: boolean;
  importance?: 'low' | 'normal' | 'high';
  parentFolderId?: string;
}
export interface BoxGraph {
  mail: { list(o?: { folder?: string; top?: number }): Promise<{ items: GraphMailSummary[] }> };
}
export type MailLoad = { state: 'ready' | 'empty'; mails: readonly BoxMail[] } | { state: 'noperm' | 'error'; mails: readonly BoxMail[] };

/** `nguyen.van@abc.com` → `nguy••••@abc.com` (như board). */
export function maskAddr(addr: string): string {
  const [u = '', d] = addr.split('@');
  return d ? `${u.slice(0, 4)}••••@${d}` : '';
}

const pad = (n: number) => String(n).padStart(2, '0');
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** Trong ngày → HH:mm · hôm qua → "Hôm qua" · cũ hơn → dd/Mon. */
export function timeLabel(iso: string, now: Date): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86_400_000);
  if (diff === 0) return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return diff === 1 ? 'Hôm qua' : `${pad(d.getDate())}/${MON[d.getMonth()]}`;
}

const JOB = /\bS[ELIN]\d{9}\b/;

export function mailFromGraph(m: GraphMailSummary, i: number, now: Date = new Date()): BoxMail {
  const subj = m.subject?.trim() || '(không có tiêu đề)';
  const addr = m.from?.emailAddress.address ?? '';
  return {
    id: 1000 + i,
    box: 'mine',
    from: m.from?.emailAddress.name?.trim() || addr.split('@')[0] || '—',
    addr: maskAddr(addr),
    subj,
    time: timeLabel(m.receivedDateTime, now),
    label: '',
    k: m.importance === 'high' ? 'K2' : undefined,
    pic: 'UNASSIGNED',
    kh: '',
    job: subj.match(JOB)?.[0] ?? '',
    bk: '',
    sla: null,
    unread: m.isRead ? 0 : 1,
    att: [],
    body: m.bodyPreview ? [m.bodyPreview] : [],
    sum: [],
    thread: 1,
  };
}

let graph: BoxGraph | undefined;
/** Hub gọi một lần sau đăng nhập; truyền `undefined` để quay về dữ liệu mẫu. */
export const setBoxGraph = (g: BoxGraph | undefined): void => void (graph = g);

/** Hộp thư đến thật (mới trước). Không có client / lỗi → dữ liệu mẫu + state; 401·403 / thiếu consent → `noperm`. */
export async function loadInbox(now: Date = new Date()): Promise<MailLoad> {
  if (!graph) return { state: 'error', mails: MAILS };
  try {
    const page = await graph.mail.list({ folder: 'inbox', top: 50 });
    const mails = page.items.map((m, i) => mailFromGraph(m, i, now));
    return mails.length ? { state: 'ready', mails } : { state: 'empty', mails: [] };
  } catch (e) {
    const x = e as { kind?: string; errorCode?: string } | null;
    const noperm = x?.kind === 'noperm' || /consent_required|interaction_required|login_required|insufficient_scope/i.test(x?.errorCode ?? '');
    return { state: noperm ? 'noperm' : 'error', mails: MAILS };
  }
}
